import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import type { IDatasource } from 'ag-grid-community';
import { useNavigate } from 'react-router-dom';
import { downloadExcelExport, EXPORT_TYPES } from '../../utils/backendExport';
import './Terminals.css';
import Loader from '../../components/shared/Loader';
import SearchableSelect from '../../components/shared/SearchableSelect';
import { BsCheckCircleFill, BsPlusCircle } from 'react-icons/bs';
import {
  LIST_CONSOLE_AG_GRID_CLASS,
  ListConsoleGridSection,
  ListConsolePage,
} from '../../components/shared/ListConsoleLayout';
import '../transaction-monitoring/TransactionMonitoring.css';
import { DataGrid, type ColDef, type GridApi } from '../../components/ui/table';
import { ActiveGridFiltersBar } from '../../components/shared/agGridCells';
import {
  GridColumnFilterButton,
  GridColumnFilterPanel,
  formatDateRangeFilterLabel,
} from '../../components/shared/GridColumnFilterPanel';
import { GridExportButton, GridSearchInput } from '../../components/shared/GridToolbar';
import { type ActiveGridFilter } from '../../utils/gridColumnFilters';
import { useAsyncEffect } from '../../hooks/useAsyncEffect';
import {
  buildAcquirerTerminalColumnFilterFields,
  buildMappingColumnFilterFields,
  buildMerchantTerminalColumnFilterFields,
  createMappingColumnDefs,
} from './table/columnDefs';
import { GRID_PAGE_SIZE, GRID_PAGE_SIZE_SELECTOR, TERMINALS_GRID_DEFAULT_COL_DEF } from './table/gridOptions';
import { Button } from '../../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Checkbox } from '../../components/ui/checkbox';
import { Label } from '../../components/ui/label';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { Switch } from '../../components/ui/switch';
import { cn } from '../../lib/utils';
import StatusFilterTabs from '../../components/shared/StatusFilterTabs';
import DeleteConfirmDialog, { type ConfirmDetailRow } from '../../components/shared/DeleteConfirmDialog';
import { showToast } from '../../store/toastStore';
import {
  ENTITY_STATUS_API_OPTIONS,
  ENTITY_STATUS_TABS,
  matchesEntityStatus,
} from '../../utils/status';
import MerchantTerminals, {
  type MerchantTerminalsState,
} from './components/merchant-terminals/MerchantTerminals';
import AcquirerTerminals, {
  type AcquirerTerminalsState,
} from './components/acquirer-terminals/AcquirerTerminals';
import {
  createTerminalMapping,
  fetchApmSchemes,
  type ApmSchemeCatalogEntry,
  deleteAcquirerTerminal,
  deleteMerchantTerminal,
  deleteTerminalMapping,
  fetchAcquirerTerminalsByAcquirerId,
  fetchActiveAcquirers,
  fetchActiveMerchants,
  fetchCountryLookups,
  fetchMerchantTerminalsByMerchantId,
  fetchTerminalMappings,
  fetchTerminalMappingsGrid,
  updateMerchantPriorityRouting,
  updateTerminalMapping,
} from './api/api';
import { buildAcquirerTerminalsRequest, buildCountryIdsByCurrency, buildCurrencyById, buildEditTerminalState, buildMappingsRequest, buildMerchantTerminalsRequest, formatCurrencyCodesLabel, formatExtraFieldsLabel, mapAgGridFilterModelToTerminalsFilters, resolveDisplayName } from './utils/utils';
import {
  ACCENT_COLORS,
  EMPTY_TERMINALS_COLUMN_FILTERS,
  SEARCH_DEBOUNCE_MS,
  TERMINALS_COLUMN_FILTER_LABELS,
} from './constants/constants';
import { type AcquirerTerminalRow, type CountryLookup, type DeleteTarget, type MappingGridRow, type MappingModalState, type MerchantTerminalRow, type TerminalRow, type TerminalsColumnFilterKey, type TerminalsColumnFilters, type TerminalsGridContext, type TerminalViewTab } from './models/types';

const matchesTerminalStatus = (status: unknown, tab: string) =>
  matchesEntityStatus(status, tab);

const PRIORITY_TIERS: Array<{ value: 1 | 2 | 3; label: string; description: string }> = [
  { value: 1, label: 'Primary', description: 'Tried first on every payment.' },
  { value: 2, label: 'Secondary', description: 'Used only if P1 is inactive.' },
  { value: 3, label: 'Fallback', description: 'Used only if P1 and P2 are inactive.' },
];

const TERMINAL_VIEW_TABS = [
  { value: 'terminal', label: 'Terminal' },
  { value: 'merchant', label: 'Merchant Terminal' },
  { value: 'acquirer', label: 'Acquirer Terminal' },
] as const;

const Terminals: React.FC = () => {
  const navigate = useNavigate();
  const [statsMids, setStatsMIDs] = useState<TerminalRow[]>([]);
  const [mids, setMIDs] = useState<TerminalRow[]>([]);
  const [loadingMIDs, setLoadingMIDs] = useState(false);
  const [tablePageSize, setTablePageSize] = useState(GRID_PAGE_SIZE);
  const [midsError, setMidsError] = useState<string | null>(null);
  const gridApiRef = useRef<GridApi | null>(null);
  const [exporting, setExporting] = useState(false);

  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('All');
  const [terminalViewTab, setTerminalViewTab] = useState<TerminalViewTab>('terminal');
  const [columnFilters, setColumnFilters] = useState<TerminalsColumnFilters>(
    EMPTY_TERMINALS_COLUMN_FILTERS,
  );
  const [filterPanelOpen, setFilterPanelOpen] = useState(false);
  const [gridFilterEpoch, setGridFilterEpoch] = useState(0);
  const skipDatasourceResetRef = useRef(false);
  const [merchantTerminalState, setMerchantTerminalState] = useState<MerchantTerminalsState>({
    rows: [],
    statsRows: [],
    loading: false,
    error: null,
  });
  const [acquirerTerminalState, setAcquirerTerminalState] = useState<AcquirerTerminalsState>({
    rows: [],
    statsRows: [],
    loading: false,
    error: null,
  });
  const [secondaryRefreshToken, setSecondaryRefreshToken] = useState(0);
  const [countryLookups, setCountryLookups] = useState<CountryLookup[]>([]);
  const [modalData, setModalData] = useState<{
    title: string;
    items: string[];
    isCurrency: boolean;
  } | null>(null);

  const currencyById = useMemo(() => buildCurrencyById(countryLookups), [countryLookups]);
  const countryIdsByCurrency = useMemo(
    () => buildCountryIdsByCurrency(countryLookups),
    [countryLookups],
  );
  const tableFiltersRef = useRef({
    filterStatus,
    search: debouncedSearch,
    columnFilters,
    countryIdsByCurrency,
  });
  const countryNameById = useMemo(
    () => countryLookups.reduce<Record<number, string>>((acc, country) => {
      acc[country.autoId] = country.countryName;
      return acc;
    }, {}),
    [countryLookups],
  );

  const handleShowMore = (
    title: string,
    items: string[],
    isCurrency?: boolean,
  ) => {
    setModalData({ title, items, isCurrency: isCurrency ?? false });
  };

  const statusTabs = ENTITY_STATUS_TABS;

  const [mappingModal, setMappingModal] = useState<MappingModalState | null>(null);
  const [mappingForm, setMappingForm] = useState({
    merchant: '',
    clientTerminal: '',
    acquirer: '',
    acquirerTerminal: '',
    status: 'ACTIVE',
    priority: '' as '' | '1' | '2' | '3',
    collectCustomerDetailsOnCheckout: false, testDummyValues: false,
    apmSchemes: [] as string[],
  });
  const [apmSchemeCatalog, setApmSchemeCatalog] = useState<ApmSchemeCatalogEntry[]>([]);
  const [mappingModalLoading, setMappingModalLoading] = useState(false);
  /** Unsaved "No Priority" choice. Null means the merchant's saved setting. */
  const [priorityRoutingDraft, setPriorityRoutingDraft] = useState<boolean | null>(null);

  const [acquirerOptions, setAcquirerOptions] = useState<Array<{ id: number; name: string }>>([]);
  const [acquirerTerminalOptions, setAcquirerTerminalOptions] = useState<Array<{ id: number; name: string; currency?: string }>>([]);
  const [merchantOptions, setMerchantOptions] = useState<Array<{ id: number; name: string; priorityRoutingEnabled: boolean }>>([]);
  const [merchantTerminalOptions, setMerchantTerminalOptions] = useState<Array<{ id: number; name: string }>>([]);
  const [loadingAcquirers, setLoadingAcquirers] = useState(false);
  const merchantLookupKey = merchantOptions.map((m) => `${m.id}\0${m.name}`).join('\0');
  const merchantById = useMemo(
    () => merchantOptions.reduce<Record<number, string>>((acc, merchant) => {
      acc[merchant.id] = merchant.name;
      return acc;
    }, {}),
    // Priority-routing edits must not rebuild this map. A new object resets the grid
    // datasource and refetches mappings before the user saves.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [merchantLookupKey],
  );
  const acquirerById = useMemo(
    () => acquirerOptions.reduce<Record<number, string>>((acc, acquirer) => {
      acc[acquirer.id] = acquirer.name;
      return acc;
    }, {}),
    [acquirerOptions],
  );
  const displayLookupsRef = useRef({ merchantById, acquirerById });
  displayLookupsRef.current = { merchantById, acquirerById };
  const [loadingAcquirerTerminals, setLoadingAcquirerTerminals] = useState(false);
  const [loadingMerchants, setLoadingMerchants] = useState(false);
  const [loadingMerchantTerminals, setLoadingMerchantTerminals] = useState(false);
  const [mappingApiError, setMappingApiError] = useState<string | null>(null);
  const [submittingMapping, setSubmittingMapping] = useState(false);
  const [mappingSuccess, setMappingSuccess] = useState(false);

  const [deleteTarget, setDeleteTarget] = useState<DeleteTarget | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  useEffect(() => {
    const timeout = window.setTimeout(() => {
      setDebouncedSearch(search);
    }, SEARCH_DEBOUNCE_MS);

    return () => window.clearTimeout(timeout);
  }, [search]);

  const handleRemoveColumnFilter = useCallback((key: string) => {
    tableFiltersRef.current = {
      ...tableFiltersRef.current,
      columnFilters: {
        ...tableFiltersRef.current.columnFilters,
        [key]: null,
      },
    };
    setColumnFilters((prev) => ({
      ...prev,
      [key]: null,
    }));
    const api = gridApiRef.current;
    if (api?.getColumnFilterModel(key)) {
      skipDatasourceResetRef.current = true;
      void api.setColumnFilterModel(key, null).then(() => {
        api.onFilterChanged();
      });
    }
  }, []);

  const handleClearColumnFilters = useCallback(() => {
    tableFiltersRef.current = {
      ...tableFiltersRef.current,
      columnFilters: EMPTY_TERMINALS_COLUMN_FILTERS,
    };
    setColumnFilters(EMPTY_TERMINALS_COLUMN_FILTERS);
    const api = gridApiRef.current;
    if (api && Object.keys(api.getFilterModel() ?? {}).length > 0) {
      skipDatasourceResetRef.current = true;
      api.setFilterModel(null);
      api.onFilterChanged();
    }
  }, []);

  const handleApplyColumnFilters = useCallback(
    (values: Record<string, string[] | null>) => {
      const nextColumnFilters = {
        ...EMPTY_TERMINALS_COLUMN_FILTERS,
        ...(Object.fromEntries(
          (Object.keys(EMPTY_TERMINALS_COLUMN_FILTERS) as TerminalsColumnFilterKey[]).map(
            (key) => [key, values[key]?.length ? values[key] : null],
          ),
        ) as TerminalsColumnFilters),
      };
      tableFiltersRef.current = {
        ...tableFiltersRef.current,
        columnFilters: nextColumnFilters,
      };
      gridApiRef.current?.setFilterModel(null);
      setColumnFilters(nextColumnFilters);
    },
    [],
  );

  const fetchStatsMIDs = useCallback(async () => {
    try {
      const { rows } = await fetchTerminalMappingsGrid({
        filterStatus: 'All',
        search: '',
        columnFilters: EMPTY_TERMINALS_COLUMN_FILTERS,
      });
      setStatsMIDs(rows);
    } catch (e) {
      console.error('Failed to fetch terminal stats:', e);
    }
  }, []);

  useEffect(() => {
    tableFiltersRef.current = {
      filterStatus,
      search: debouncedSearch,
      columnFilters,
      countryIdsByCurrency,
    };
  }, [filterStatus, debouncedSearch, columnFilters, countryIdsByCurrency]);

  const tableDatasource = useMemo<IDatasource>(
    () => ({
      getRows: async (params) => {
        const pageSize = params.endRow - params.startRow;
        const page = pageSize > 0 ? Math.floor(params.startRow / pageSize) : 0;
        setLoadingMIDs(true);
        setMidsError(null);
        try {
          const rawFilterModel =
            (params.filterModel as Record<string, unknown> | null | undefined) ??
            (params.api?.getFilterModel() as Record<string, unknown> | null | undefined) ??
            null;
          const nativeColumnFilters = mapAgGridFilterModelToTerminalsFilters(rawFilterModel);
          const mergedColumnFilters: TerminalsColumnFilters = {
            ...tableFiltersRef.current.columnFilters,
            ...nativeColumnFilters,
          };
          const { rows, totalElements } = await fetchTerminalMappings(
            buildMappingsRequest({
              filterStatus: tableFiltersRef.current.filterStatus,
              search: tableFiltersRef.current.search,
              columnFilters: mergedColumnFilters,
              page,
              size: pageSize,
            }),
          );
          const { merchantById: merchantsById, acquirerById: acquirersById } =
            displayLookupsRef.current;
          const maxVol = Math.max(...rows.map((m) => m.volume), 1);
          const gridRows: MappingGridRow[] = rows.map((m, idx) => ({
            id: m.id,
            merchantTerminalName: m.merchantTerminalName,
            acquirerTerminalName: m.acquirerTerminalName,
            merchantName: resolveDisplayName(m.merchantName, merchantsById, m.merchantId),
            acquirerName: resolveDisplayName(m.acquirerName, acquirersById, m.acquirerId),
            status: m.status,
            volume: m.volume,
            accent: ACCENT_COLORS[idx % ACCENT_COLORS.length],
            volPct: Math.round((m.volume / maxVol) * 100),
            priority: m.priority,
            createdAt: m.createdAt,
            updatedAt: m.updatedAt,
            createdBy: m.createdBy,
            updatedBy: m.updatedBy,
          }));
          setMIDs(rows);
          params.successCallback(gridRows, totalElements);
        } catch (e) {
          console.error(e);
          setMIDs([]);
          setMidsError('Could not load terminals. Please try again.');
          params.failCallback();
        } finally {
          setLoadingMIDs(false);
        }
      },
    }),
    [],
  );

  useEffect(() => {
    if (terminalViewTab !== 'terminal') return;
    if (skipDatasourceResetRef.current) {
      skipDatasourceResetRef.current = false;
      return;
    }
    gridApiRef.current?.setGridOption('datasource', tableDatasource);
  }, [terminalViewTab, filterStatus, debouncedSearch, columnFilters, merchantById, acquirerById, tableDatasource]);

  const refreshMIDs = useCallback(async () => {
    if (terminalViewTab === 'terminal') {
      gridApiRef.current?.refreshInfiniteCache();
    }
    await fetchStatsMIDs();
  }, [fetchStatsMIDs, terminalViewTab]);

  const loadCountryLookups = useCallback(async () => {
    try {
      setCountryLookups(await fetchCountryLookups());
    } catch (e) {
      console.error('Failed to load countries:', e);
    }
  }, []);

  const loadActiveAcquirers = useCallback(async () => {
    setLoadingAcquirers(true);
    setMappingApiError(null);
    try {
      setAcquirerOptions(await fetchActiveAcquirers());
    } catch (e) {
      console.error(e);
      setMappingApiError('Could not load acquirers.');
    } finally {
      setLoadingAcquirers(false);
    }
  }, []);

  const loadAcquirerTerminalsForMapping = useCallback(async (acquirerId: number) => {
    if (!acquirerId) {
      setAcquirerTerminalOptions([]);
      return;
    }
    setLoadingAcquirerTerminals(true);
    setMappingApiError(null);
    try {
      setAcquirerTerminalOptions(await fetchAcquirerTerminalsByAcquirerId(acquirerId));
    } catch (e) {
      console.error(e);
      setAcquirerTerminalOptions([]);
      setMappingApiError('Could not load acquirer terminals.');
    } finally {
      setLoadingAcquirerTerminals(false);
    }
  }, []);

  const loadMerchantsForMapping = useCallback(async () => {
    setLoadingMerchants(true);
    setMappingApiError(null);
    try {
      setMerchantOptions(await fetchActiveMerchants());
    } catch (e) {
      console.error(e);
      setMappingApiError('Could not load merchants.');
    } finally {
      setLoadingMerchants(false);
    }
  }, []);

  const loadMerchantTerminalsForMapping = useCallback(async (merchantId: number) => {
    if (!merchantId) {
      setMerchantTerminalOptions([]);
      return;
    }
    setLoadingMerchantTerminals(true);
    setMappingApiError(null);
    try {
      setMerchantTerminalOptions(await fetchMerchantTerminalsByMerchantId(merchantId));
    } catch (e) {
      console.error(e);
      setMerchantTerminalOptions([]);
      setMappingApiError('Could not load merchant terminals for this merchant.');
    } finally {
      setLoadingMerchantTerminals(false);
    }
  }, []);

  const invalidateSecondaryTerminalData = useCallback(() => {
    setSecondaryRefreshToken((token) => token + 1);
  }, []);

  useAsyncEffect(async (isActive) => {
    await fetchStatsMIDs();
    if (!isActive()) return;
  }, [fetchStatsMIDs]);

  useAsyncEffect(async (isActive) => {
    await Promise.all([
      loadActiveAcquirers(),
      loadMerchantsForMapping(),
      loadCountryLookups(),
    ]);
    if (!isActive()) return;
  }, [loadActiveAcquirers, loadMerchantsForMapping, loadCountryLookups]);

  // The APM scheme catalog (Collect Group mapping checkboxes) — fetched once from the backend so it
  // never drifts from what the hosted checkout actually offers. Best-effort: on failure the
  // checkboxes simply don't render and the mapping still saves without an APM selection.
  useAsyncEffect(async (isActive) => {
    try {
      const schemes = await fetchApmSchemes();
      if (isActive()) setApmSchemeCatalog(schemes);
    } catch {
      if (isActive()) setApmSchemeCatalog([]);
    }
  }, []);

  const handleMappingFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    if (name === 'acquirer') {
      setMappingForm((prev) => ({ ...prev, acquirer: value, acquirerTerminal: '' }));
      const aid = Number(value);
      if (aid) loadAcquirerTerminalsForMapping(aid);
      else setAcquirerTerminalOptions([]);
      return;
    }
    if (name === 'merchant') {
      const mid = Number(value);
      const merchant = merchantOptions.find((m) => m.id === mid);
      const nextPriority = merchant?.priorityRoutingEnabled
        ? getFirstAvailablePriority(mid)
        : '';
      setPriorityRoutingDraft(null);
      setMappingForm((prev) => ({
        ...prev,
        merchant: value,
        clientTerminal: '',
        priority: nextPriority,
      }));
      if (mid) loadMerchantTerminalsForMapping(mid);
      else setMerchantTerminalOptions([]);
      return;
    }
    setMappingForm((prev) => ({ ...prev, [name]: value }));
  };

  const persistPriorityRoutingDraft = async (merchantId: number) => {
    if (priorityRoutingDraft == null || !merchantId) return;
    const saved = merchantOptions.find((m) => m.id === merchantId)?.priorityRoutingEnabled ?? false;
    if (priorityRoutingDraft === saved) return;
    const nextEnabled = priorityRoutingDraft;
    await updateMerchantPriorityRouting(merchantId, nextEnabled);
    setMerchantOptions((prev) =>
      prev.map((m) =>
        m.id === merchantId ? { ...m, priorityRoutingEnabled: nextEnabled } : m,
      ),
    );
    setPriorityRoutingDraft(null);
  };

  const handleMappingSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!mappingModal) return;
    setMappingApiError(null);
    setMappingSuccess(false);

    if (mappingModal.mode === 'create') {
      if (!mappingForm.merchant || !mappingForm.clientTerminal || !mappingForm.acquirer || !mappingForm.acquirerTerminal) {
        setMappingApiError('Please select all required fields.');
        return;
      }
      // No required-priority check here anymore: leaving priority blank (the "Supplementary"
      // tile) is a deliberate, valid choice for a priority-routing merchant, not a missed field.

      setSubmittingMapping(true);
      try {
        await persistPriorityRoutingDraft(Number(mappingForm.merchant));
        const res: any = await createTerminalMapping({
          merchantId: Number(mappingForm.merchant),
          merchantTerminalId: Number(mappingForm.clientTerminal),
          acquirerId: Number(mappingForm.acquirer),
          acquirerTerminalId: Number(mappingForm.acquirerTerminal),
          priority: isPriorityMerchant && mappingForm.priority ? Number(mappingForm.priority) : undefined,
          collectCustomerDetailsOnCheckout: mappingForm.collectCustomerDetailsOnCheckout,
          // Bypass KYC disabled 2026-10-28: always send false so saving clears any legacy value.
          testDummyValues: false,
          apmSchemes: mappingForm.apmSchemes,
        });
        if (res?.success) {
          setMappingSuccess(true);
          setMappingForm({ merchant: '', clientTerminal: '', acquirer: '', acquirerTerminal: '', status: 'ACTIVE', priority: '', collectCustomerDetailsOnCheckout: false, testDummyValues: false, apmSchemes: [] });
          setMerchantTerminalOptions([]);
          setAcquirerTerminalOptions([]);
          invalidateSecondaryTerminalData();
          await refreshMIDs();
          setTimeout(() => { closeMappingModal(); }, 1500);
        } else {
          setMappingApiError(res?.message || 'Failed to create terminal mapping.');
        }
      } catch (err: any) {
        console.error('Failed to create terminal mapping', err);
        setMappingApiError(err.response?.data?.message || err.message || 'Failed to create terminal mapping. Please try again.');
      } finally {
        setSubmittingMapping(false);
      }
      return;
    }

    if (!mappingForm.clientTerminal || !mappingForm.acquirerTerminal) {
      setMappingApiError('Please select all required fields.');
      return;
    }
    setSubmittingMapping(true);
    try {
      await persistPriorityRoutingDraft(Number(mappingForm.merchant));
      const res: any = await updateTerminalMapping({
        mappingId: mappingModal.row.id,
        merchantId: Number(mappingForm.merchant),
        acquirerId: Number(mappingForm.acquirer),
        merchantTerminalId: Number(mappingForm.clientTerminal),
        acquirerTerminalId: Number(mappingForm.acquirerTerminal),
        status: mappingForm.status,
        priority: isPriorityMerchant && mappingForm.priority ? Number(mappingForm.priority) : undefined,
        collectCustomerDetailsOnCheckout: mappingForm.collectCustomerDetailsOnCheckout,
        // Bypass KYC disabled 2026-10-28: always send false so saving clears any legacy value.
        testDummyValues: false,
        apmSchemes: mappingForm.apmSchemes,
      });

      if (res?.success === false) {
        throw new Error(res?.message || 'Failed to save changes.');
      }

      showToast('Changes saved successfully.', 'success');
      closeMappingModal();
      invalidateSecondaryTerminalData();
      await refreshAfterMutation();
    } catch (err: any) {
      console.error('Failed to update terminal record:', err);
      const message = err.response?.data?.message || err.message || 'Failed to save changes.';
      setMappingApiError(message);
      showToast(message, 'error');
    } finally {
      setSubmittingMapping(false);
    }
  };

  const onMappingSelectChange = (name: string, value: string) => {
    handleMappingFormChange({ target: { name, value } } as React.ChangeEvent<HTMLSelectElement>);
  };

  const selectedMappingMerchant = useMemo(
    () => merchantOptions.find((m) => String(m.id) === mappingForm.merchant),
    [merchantOptions, mappingForm.merchant],
  );
  const isPriorityMerchant =
    priorityRoutingDraft ?? selectedMappingMerchant?.priorityRoutingEnabled ?? false;

  // Priorities already used by one of this merchant's OTHER mappings: computed from the
  // already-loaded mapping grid data, no extra request needed. Excludes the mapping currently
  // being edited so it doesn't lock out its own priority.
  const takenPriorities = useMemo(() => {
    const merchantId = Number(mappingForm.merchant);
    if (!merchantId) return new Set<number>();
    return new Set(
      statsMids
        .filter((m) => m.merchantId === merchantId && m.priority != null)
        .filter((m) => !(mappingModal?.mode === 'edit' && m.id === mappingModal.row.id))
        .map((m) => m.priority as number),
    );
  }, [statsMids, mappingForm.merchant, mappingModal]);

  // The merchant's existing Bolt-on add-on mapping (priority === null), if any: mirrors
  // takenPriorities but for the Bolt-on tile. Without this, clicking "Bolt-on add-on" while
  // editing a DIFFERENT (e.g. P1) mapping only cleared the priority field in the currently-open
  // form; it never looked up the merchant's actual saved bolt-on row, so the tile always showed
  // whatever terminal/acquirer the form already had selected instead of that row's real data:
  // looking "static" and never reflecting what was actually saved. Only shows the first one found
  // if a merchant somehow has more than one.
  const existingBoltOnRow = useMemo(() => {
    const merchantId = Number(mappingForm.merchant);
    if (!merchantId) return null;
    return (
      statsMids.find(
        (m) =>
          m.merchantId === merchantId
          && m.priority == null
          && !(mappingModal?.mode === 'edit' && m.id === mappingModal.row.id),
      ) ?? null
    );
  }, [statsMids, mappingForm.merchant, mappingModal]);

  const getFirstAvailablePriority = (
    merchantId: number,
    excludeMappingId?: number,
  ): '' | '1' | '2' | '3' => {
    const taken = new Set(
      statsMids
        .filter((m) => m.merchantId === merchantId && m.priority != null && m.id !== excludeMappingId)
        .map((m) => m.priority as number),
    );
    if (!taken.has(1)) return '1';
    if (!taken.has(2)) return '2';
    if (!taken.has(3)) return '3';
    return '';
  };

  const handleTogglePriorityRouting = (noPriorityOn: boolean) => {
    const merchantId = Number(mappingForm.merchant);
    if (!merchantId) return;
    const nextPriorityRoutingEnabled = !noPriorityOn;
    const restoredPriority =
      mappingModal?.mode === 'edit' && mappingModal.row.priority
        ? (String(mappingModal.row.priority) as '1' | '2' | '3')
        : '';

    setMappingApiError(null);
    setPriorityRoutingDraft(nextPriorityRoutingEnabled);
    if (!nextPriorityRoutingEnabled) {
      setMappingForm((prev) => ({ ...prev, priority: '' }));
    } else if (!mappingForm.priority) {
      const excludeId = mappingModal?.mode === 'edit' ? mappingModal.row.id : undefined;
      setMappingForm((prev) => ({
        ...prev,
        priority: restoredPriority || getFirstAvailablePriority(merchantId, excludeId),
      }));
    }
  };

  const merchantSelectOptions = useMemo(
    () => merchantOptions.map((m) => ({ value: String(m.id), label: m.name })),
    [merchantOptions],
  );

  const acquirerSelectOptions = useMemo(
    () => acquirerOptions.map((a) => ({ value: String(a.id), label: a.name })),
    [acquirerOptions],
  );

  // APM selection is Collect Group (CollectNExchange) only. Match on the selected acquirer's name —
  // "Collect Group" and "CollectNExchange" both contain "collect" — so the per-mapping APM
  // checkboxes only appear for that acquirer, mirroring how checkout only offers APMs there.
  const isCollectGroupMappingAcquirer = useMemo(() => {
    const name = acquirerById[Number(mappingForm.acquirer)] ?? '';
    return /collect/i.test(name);
  }, [acquirerById, mappingForm.acquirer]);

  const merchantTerminalSelectOptions = useMemo(
    () => merchantTerminalOptions.map((t) => ({ value: String(t.id), label: t.name })),
    [merchantTerminalOptions],
  );

  const acquirerTerminalSelectOptions = useMemo(
    () =>
      acquirerTerminalOptions.map((t) => ({
        value: String(t.id),
        label: `${t.name}${t.currency ? ` (${t.currency})` : ''}`,
      })),
    [acquirerTerminalOptions],
  );

  const openCreateMappingModal = () => {
    setPriorityRoutingDraft(null);
    setMappingModal({ mode: 'create' });
    setMappingForm({ merchant: '', clientTerminal: '', acquirer: '', acquirerTerminal: '', status: 'ACTIVE', priority: '', collectCustomerDetailsOnCheckout: false, testDummyValues: false, apmSchemes: [] });
    setMerchantTerminalOptions([]);
    setAcquirerTerminalOptions([]);
    setMappingApiError(null);
    setMappingSuccess(false);
    setMappingModalLoading(false);
  };

  const openEditMappingModal = async (row: TerminalRow) => {
    setPriorityRoutingDraft(null);
    setMappingModal({ mode: 'edit', row });
    setMappingApiError(null);
    setMappingSuccess(false);
    setMappingModalLoading(true);
    setMappingForm({
      merchant: String(row.merchantId),
      clientTerminal: String(row.merchantTerminalId || ''),
      acquirer: String(row.acquirerId),
      acquirerTerminal: String(row.acquirerTerminalId || ''),
      status: row.status,
      priority: row.priority ? (String(row.priority) as '1' | '2' | '3') : '',
      collectCustomerDetailsOnCheckout: row.collectCustomerDetailsOnCheckout,
      testDummyValues: row.testDummyValues,
      apmSchemes: row.apmSchemes ?? [],
    });
    // Seed/correct merchantOptions from the row's own priorityRoutingEnabled (the merchant's real
    // setting, from the backend): never from row.priority != null. A priority-routing merchant's
    // Bolt-on add-on mapping deliberately has no priority, so that inference used to report such a
    // merchant as non-priority-routing, hiding the P1-P3/Bolt-on tiles entirely when editing it.
    setMerchantOptions((prev) => {
      const existing = prev.find((m) => m.id === row.merchantId);
      if (!existing) {
        return [
          ...prev,
          {
            id: row.merchantId,
            name: row.merchantName,
            priorityRoutingEnabled: row.priorityRoutingEnabled,
          },
        ];
      }
      if (existing.priorityRoutingEnabled !== row.priorityRoutingEnabled) {
        return prev.map((m) =>
          m.id === row.merchantId ? { ...m, priorityRoutingEnabled: row.priorityRoutingEnabled } : m,
        );
      }
      return prev;
    });
    try {
      await Promise.all([
        loadMerchantTerminalsForMapping(row.merchantId),
        loadAcquirerTerminalsForMapping(row.acquirerId),
      ]);
      if (row.merchantTerminalId) {
        setMerchantTerminalOptions((prev) =>
          prev.some((t) => t.id === row.merchantTerminalId)
            ? prev
            : [{ id: row.merchantTerminalId, name: row.merchantTerminalName }, ...prev],
        );
      }
      if (row.acquirerTerminalId) {
        setAcquirerTerminalOptions((prev) =>
          prev.some((t) => t.id === row.acquirerTerminalId)
            ? prev
            : [{ id: row.acquirerTerminalId, name: row.acquirerTerminalName }, ...prev],
        );
      }
    } catch (e) {
      console.error(e);
      setMappingApiError('Could not load terminal details.');
    } finally {
      setMappingModalLoading(false);
    }
  };

  const closeMappingModal = () => {
    if (submittingMapping) return;
    setPriorityRoutingDraft(null);
    setMappingModal(null);
    setMappingForm({ merchant: '', clientTerminal: '', acquirer: '', acquirerTerminal: '', status: 'ACTIVE', priority: '', collectCustomerDetailsOnCheckout: false, testDummyValues: false, apmSchemes: [] });
    setMerchantTerminalOptions([]);
    setAcquirerTerminalOptions([]);
    setMappingApiError(null);
    setMappingSuccess(false);
    setMappingModalLoading(false);
  };

  const closeDeleteDialog = () => {
    if (deleting) return;
    setDeleteTarget(null);
    setDeleteError(null);
  };

  const getDeleteDialogMeta = (target: DeleteTarget) => {
    if (target.type === 'mapping') {
      return {
        title: 'Delete Terminal Mapping',
        entityLabel: 'mapping',
        entityName: `${target.row.merchantTerminalName} → ${target.row.acquirerTerminalName}`,
        details: [
          { label: 'Mapping ID', value: target.row.id },
          { label: 'Merchant Terminal', value: target.row.merchantTerminalName },
          { label: 'Acquirer Terminal', value: target.row.acquirerTerminalName },
          { label: 'Status', value: target.row.status },
        ] satisfies ConfirmDetailRow[],
      };
    }
    if (target.type === 'merchant') {
      return {
        title: 'Delete Merchant Terminal',
        entityLabel: 'merchant terminal',
        entityName: target.row.name,
        details: [
          { label: 'Terminal ID', value: target.row.terminalId },
          { label: 'Merchant', value: resolveDisplayName(target.row.merchantName, merchantById, target.row.merchantId) },
          { label: 'Country', value: resolveDisplayName(target.row.countryName, countryNameById, target.row.countryId) },
          { label: 'Currencies', value: formatCurrencyCodesLabel(target.row.currencies, currencyById) },
          { label: 'Notes', value: target.row.notes.trim() || '—' },
        ] satisfies ConfirmDetailRow[],
      };
    }
    return {
      title: 'Delete Acquirer Terminal',
      entityLabel: 'acquirer terminal',
      entityName: target.row.name,
      details: [
        { label: 'Terminal ID', value: target.row.terminalId },
        { label: 'Acquirer', value: resolveDisplayName(target.row.acquirerName, acquirerById, target.row.acquirerId) },
        { label: 'Country', value: resolveDisplayName(target.row.countryName, countryNameById, target.row.countryId) },
        { label: 'Currencies', value: formatCurrencyCodesLabel(target.row.currencies, currencyById) },
        { label: 'Extra Fields', value: formatExtraFieldsLabel(target.row.extraFields) },
      ] satisfies ConfirmDetailRow[],
    };
  };

  const refreshAfterMutation = async () => {
    if (terminalViewTab === 'terminal') {
      await refreshMIDs();
      return;
    }
    invalidateSecondaryTerminalData();
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;

    setDeleting(true);
    setDeleteError(null);
    try {
      let res: any;
      if (deleteTarget.type === 'mapping') {
        res = await deleteTerminalMapping(deleteTarget.row.id);
      } else if (deleteTarget.type === 'merchant') {
        res = await deleteMerchantTerminal(deleteTarget.row.terminalId);
      } else {
        res = await deleteAcquirerTerminal(deleteTarget.row.terminalId);
      }

      if (res?.success === false) {
        throw new Error(res?.message || 'Failed to delete.');
      }

      const label =
        deleteTarget.type === 'mapping'
          ? 'Terminal mapping'
          : deleteTarget.type === 'merchant'
            ? 'Merchant terminal'
            : 'Acquirer terminal';
      setDeleteTarget(null);
      showToast(`${label} deleted successfully.`, 'success');
      invalidateSecondaryTerminalData();
      await refreshAfterMutation();
    } catch (err: any) {
      console.error('Failed to delete terminal record:', err);
      const message = err.response?.data?.message || err.message || 'Failed to delete.';
      setDeleteError(message);
      showToast(message, 'error');
    } finally {
      setDeleting(false);
    }
  };

  const handleEditTerminalClick = (
    target: Extract<DeleteTarget, { type: 'merchant' } | { type: 'acquirer' }>,
  ) => {
    navigate('/dashboard/terminals/create', {
      state: buildEditTerminalState(target),
    });
  };

  const handleTerminalViewTabChange = (nextTab: TerminalViewTab) => {
    if (nextTab === terminalViewTab) return;
    skipDatasourceResetRef.current = false;
    setTerminalViewTab(nextTab);
    setSearch('');
    setDebouncedSearch('');
    setColumnFilters(EMPTY_TERMINALS_COLUMN_FILTERS);
    gridApiRef.current?.setFilterModel(null);
    setGridFilterEpoch((n) => n + 1);
  };

  const isTerminalTab = terminalViewTab === 'terminal';
  const isMerchantTab = terminalViewTab === 'merchant';
  const isAcquirerTab = terminalViewTab === 'acquirer';
  const tableLoading = isTerminalTab
    ? loadingMIDs
    : isMerchantTab
      ? merchantTerminalState.loading
      : acquirerTerminalState.loading;
  const tableError = isTerminalTab
    ? midsError
    : isMerchantTab
      ? merchantTerminalState.error
      : acquirerTerminalState.error;

  const handleGridFilterChanged = useCallback(() => {
    setGridFilterEpoch((n) => n + 1);
  }, []);

  const handleGridReady = useCallback((event: { api: GridApi }) => {
    gridApiRef.current = event.api;
  }, []);

  const handleTablePaginationChanged = useCallback(() => {
    const api = gridApiRef.current;
    if (!api || terminalViewTab !== 'terminal') return;
    const newSize = api.paginationGetPageSize();
    if (newSize && newSize !== tablePageSize) {
      setTablePageSize(newSize);
    }
  }, [tablePageSize, terminalViewTab]);

  const mergedColumnFilters = useMemo<TerminalsColumnFilters>(() => {
    void gridFilterEpoch;
    const nativeColumnFilters = mapAgGridFilterModelToTerminalsFilters(
      gridApiRef.current?.getFilterModel() as Record<string, unknown> | null | undefined,
    );
    return {
      ...columnFilters,
      ...nativeColumnFilters,
    };
  }, [columnFilters, gridFilterEpoch]);

  const activeColumnFilters = useMemo<ActiveGridFilter[]>(() => {
    return (Object.entries(mergedColumnFilters) as [TerminalsColumnFilterKey, string[] | null][])
      .filter(([, value]) => Boolean(value?.length))
      .map(([key, value]) => ({
        key,
        label: TERMINALS_COLUMN_FILTER_LABELS[key],
        value:
          key === 'createdAt'
            ? formatDateRangeFilterLabel(value)
            : (value as string[]).join(', '),
      }));
  }, [mergedColumnFilters]);

  const merchantTerminals = merchantTerminalState.rows;
  const statsMerchantTerminals = merchantTerminalState.statsRows;
  const acquirerTerminals = acquirerTerminalState.rows;
  const statsAcquirerTerminals = acquirerTerminalState.statsRows;

  const handleMerchantTerminalsStateChange = useCallback((state: MerchantTerminalsState) => {
    setMerchantTerminalState(state);
  }, []);

  const handleAcquirerTerminalsStateChange = useCallback((state: AcquirerTerminalsState) => {
    setAcquirerTerminalState(state);
  }, []);

  const rawColumnFilterFields = useMemo(() => {
    const terminalFilterSource =
      statsMids.length > 0 ? statsMids : mids;
    const merchantFilterSource =
      statsMerchantTerminals.length > 0 ? statsMerchantTerminals : merchantTerminals;
    const acquirerFilterSource =
      statsAcquirerTerminals.length > 0 ? statsAcquirerTerminals : acquirerTerminals;

    if (isTerminalTab) {
      return buildMappingColumnFilterFields(terminalFilterSource, {
        merchantById,
        acquirerById,
      });
    }

    if (isMerchantTab) {
      return buildMerchantTerminalColumnFilterFields(merchantFilterSource, {
        merchantById,
        countryNameById,
        currencyById,
      });
    }

    return buildAcquirerTerminalColumnFilterFields(acquirerFilterSource, {
      acquirerById,
      countryNameById,
      currencyById,
    });
  }, [
    isTerminalTab,
    isMerchantTab,
    mids,
    statsMids,
    merchantTerminals,
    statsMerchantTerminals,
    acquirerTerminals,
    statsAcquirerTerminals,
    countryNameById,
    merchantById,
    acquirerById,
    currencyById,
  ]);

  const columnFilterFields = useMemo(() => {
    return rawColumnFilterFields.map((field) => {
      if (field.type === 'dateRange') return field;
      const applied = mergedColumnFilters[field.key];
      if (!applied?.length) return field;
      const optionValues = field.options.map((option) =>
        typeof option === 'string' ? option : option.value,
      );
      const missing = applied.filter((value) => !optionValues.includes(value));
      if (!missing.length) return field;
      return {
        ...field,
        options: [...field.options, ...missing],
      };
    });
  }, [rawColumnFilterFields, mergedColumnFilters]);

  const mappingColumnDefs = useMemo<ColDef<MappingGridRow>[]>(
    () => createMappingColumnDefs(),
    [],
  );

  const terminalsGridContext = useMemo<TerminalsGridContext>(
    () => ({
      onShowMore: handleShowMore,
      mappingActions: {
        onEdit: (row) => {
          const mapping = mids.find((m) => m.id === row.id);
          if (mapping) void openEditMappingModal(mapping);
        },
        onDelete: (row) => {
          const mapping = mids.find((m) => m.id === row.id);
          if (mapping) {
            setDeleteError(null);
            setDeleteTarget({ type: 'mapping', row: mapping });
          }
        },
      },
      terminalActions: {
        onEdit: (raw) => {
          if (!raw || typeof raw !== 'object') return;
          if ('merchantId' in raw) {
            handleEditTerminalClick({ type: 'merchant', row: raw as MerchantTerminalRow });
          } else if ('acquirerId' in raw) {
            handleEditTerminalClick({ type: 'acquirer', row: raw as AcquirerTerminalRow });
          }
        },
        onDelete: (raw) => {
          if (!raw || typeof raw !== 'object') return;
          setDeleteError(null);
          if ('merchantId' in raw) {
            setDeleteTarget({ type: 'merchant', row: raw as MerchantTerminalRow });
          } else if ('acquirerId' in raw) {
            setDeleteTarget({ type: 'acquirer', row: raw as AcquirerTerminalRow });
          }
        },
      },
    }),
    [mids],
  );

  const gridEmptyMessage = isTerminalTab
    ? mids.length === 0
      ? 'No terminals found.'
      : 'No terminals match the current filters.'
    : isMerchantTab
      ? merchantTerminals.length === 0
        ? 'No merchant terminals found.'
        : 'No merchant terminals match the current filters.'
      : acquirerTerminals.length === 0
        ? 'No acquirer terminals found.'
        : 'No acquirer terminals match the current filters.';

  const handleExport = async () => {
    setExporting(true);
    try {
      const nativeColumnFilters = mapAgGridFilterModelToTerminalsFilters(
        gridApiRef.current?.getFilterModel() as Record<string, unknown> | null | undefined,
      );
      const merged: TerminalsColumnFilters = {
        ...columnFilters,
        ...nativeColumnFilters,
      };
      if (isTerminalTab) {
        await downloadExcelExport({
          type: EXPORT_TYPES.TERMINALS,
          fileNamePrefix: 'terminals',
          errorMessage: 'Failed to export terminals.',
          payload: buildMappingsRequest({
            filterStatus,
            search: debouncedSearch,
            columnFilters: merged,
          }),
        });
      } else if (isMerchantTab) {
        await downloadExcelExport({
          type: EXPORT_TYPES.MERCHANT_TERMINALS,
          fileNamePrefix: 'merchant-terminals',
          errorMessage: 'Failed to export terminals.',
          payload: buildMerchantTerminalsRequest({
            search: debouncedSearch,
            columnFilters: merged,
            countryIdsByCurrency,
          }),
        });
      } else {
        await downloadExcelExport({
          type: EXPORT_TYPES.ACQUIRER_TERMINALS,
          fileNamePrefix: 'acquirer-terminals',
          errorMessage: 'Failed to export terminals.',
          payload: buildAcquirerTerminalsRequest({
            search: debouncedSearch,
            columnFilters: merged,
            countryIdsByCurrency,
          }),
        });
      }
    } finally {
      setExporting(false);
    }
  };

  const hasExportRows = isTerminalTab
    ? mids.length > 0
    : isMerchantTab
      ? merchantTerminals.length > 0
      : acquirerTerminals.length > 0;

  const searchPlaceholder = isTerminalTab
    ? 'Search terminal, acquirer or mapping ID…'
    : isMerchantTab
      ? 'Search merchant terminal, ID, or notes…'
      : 'Search acquirer terminal, ID, or extra fields…';

  return (
    <ListConsolePage
      title="Terminals"
      error={tableError}
      actions={
        <div className="flex flex-wrap items-center gap-2">
          <StatusFilterTabs
            tabs={[...TERMINAL_VIEW_TABS]}
            value={terminalViewTab}
            onChange={(value) => handleTerminalViewTabChange(value as TerminalViewTab)}
            disabled={tableLoading}
            className="w-auto shrink-0"
          />
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="tm-console-action-btn tm-console-action-btn--outline gap-1.5"
            onClick={openCreateMappingModal}
          >
            <BsPlusCircle className="h-3 w-3 shrink-0" aria-hidden />
            Direct Mapping
          </Button>
          <Button
            type="button"
            size="sm"
            className="tm-console-action-btn gap-1.5"
            onClick={() => navigate('/dashboard/terminals/create')}
          >
            <BsPlusCircle className="h-3 w-3 shrink-0" aria-hidden />
            Create Terminal
          </Button>
        </div>
      }
    >
      <ListConsoleGridSection
        toolbarLeft={
          isTerminalTab ? (
            <StatusFilterTabs
              tabs={statusTabs}
              value={filterStatus}
              onChange={setFilterStatus}
              disabled={tableLoading}
              getCount={(tab) => {
                const key = typeof tab === 'string' ? tab : tab.value;
                if (key === 'All') return statsMids.length;
                return statsMids.filter((item) =>
                  matchesTerminalStatus(item.status, key),
                ).length;
              }}
              className="w-auto shrink-0"
            />
          ) : undefined
        }
        toolbarRight={
          <div className="tm-toolbar-controls">
            <GridSearchInput
              value={search}
              onValueChange={setSearch}
              placeholder={searchPlaceholder}
              disabled={tableLoading}
              className="tm-toolbar-search"
            />
            <GridColumnFilterButton
              activeCount={activeColumnFilters.length}
              onClick={() => setFilterPanelOpen(true)}
              disabled={tableLoading}
              className="tm-compact-icon-btn"
            />
            <GridExportButton
              onClick={handleExport}
              loading={exporting}
              disabled={tableLoading || !hasExportRows}
              className="tm-compact-icon-btn"
            />
          </div>
        }
        filterBar={
          <ActiveGridFiltersBar
            filters={activeColumnFilters}
            onRemove={handleRemoveColumnFilter}
            onClearAll={handleClearColumnFilters}
          />
        }
      >
        {isTerminalTab && (
          <DataGrid<MappingGridRow>
            rowModelType="infinite"
            datasource={tableDatasource}
            cacheBlockSize={tablePageSize}
            columnDefs={mappingColumnDefs}
            context={terminalsGridContext}
            loading={tableLoading}
            pagination
            paginationPageSize={tablePageSize}
            paginationPageSizeSelector={GRID_PAGE_SIZE_SELECTOR}
            height="100%"
            className={LIST_CONSOLE_AG_GRID_CLASS}
            onGridReady={handleGridReady}
            defaultColDef={TERMINALS_GRID_DEFAULT_COL_DEF}
            gridOptions={{
              suppressRowClickSelection: true,
              getRowId: (params) => String(params.data?.id ?? ''),
              overlayNoRowsTemplate: `<span class="text-sm text-muted-foreground">${gridEmptyMessage}</span>`,
              onFilterChanged: handleGridFilterChanged,
              onPaginationChanged: handleTablePaginationChanged,
            }}
          />
        )}
        {isMerchantTab && (
          <MerchantTerminals
            active={isMerchantTab}
            debouncedSearch={debouncedSearch}
            columnFilters={columnFilters}
            countryLookups={countryLookups}
            countryIdsByCurrency={countryIdsByCurrency}
            merchantOptions={merchantOptions}
            gridContext={terminalsGridContext}
            gridApiRef={gridApiRef}
            emptyMessage={gridEmptyMessage}
            onStateChange={handleMerchantTerminalsStateChange}
            refreshToken={secondaryRefreshToken}
            gridFilterEpoch={gridFilterEpoch}
            skipDatasourceResetRef={skipDatasourceResetRef}
            onGridFilterChanged={handleGridFilterChanged}
          />
        )}
        {isAcquirerTab && (
          <AcquirerTerminals
            active={isAcquirerTab}
            debouncedSearch={debouncedSearch}
            columnFilters={columnFilters}
            countryLookups={countryLookups}
            countryIdsByCurrency={countryIdsByCurrency}
            acquirerOptions={acquirerOptions}
            gridContext={terminalsGridContext}
            gridApiRef={gridApiRef}
            emptyMessage={gridEmptyMessage}
            onStateChange={handleAcquirerTerminalsStateChange}
            refreshToken={secondaryRefreshToken}
            gridFilterEpoch={gridFilterEpoch}
            skipDatasourceResetRef={skipDatasourceResetRef}
            onGridFilterChanged={handleGridFilterChanged}
          />
        )}
      </ListConsoleGridSection>

      <Dialog
        open={!!mappingModal}
        onOpenChange={(open) => {
          if (!open) closeMappingModal();
        }}
      >
        {mappingModal && (
          <DialogContent className="flex max-h-[80vh] flex-col overflow-hidden sm:max-w-[600px] p-0">
            <form onSubmit={handleMappingSubmit} className="flex min-h-0 flex-1 flex-col">
              <DialogHeader className="shrink-0 p-4">
                <DialogTitle>
                  {mappingModal.mode === 'create' ? 'Create Direct Mapping' : 'Edit Terminal Mapping'}
                </DialogTitle>
                <DialogDescription>
                  {mappingModal.mode === 'create'
                    ? 'Map merchant and acquirer terminals'
                    : 'Update the merchant and acquirer terminal mapping'}
                </DialogDescription>
              </DialogHeader>

              {mappingApiError && (
                <Alert variant="destructive" className="px-4 py-2">
                  <AlertDescription>{mappingApiError}</AlertDescription>
                </Alert>
              )}
              {mappingSuccess && (
                <Alert className="border-green-500/50 bg-green-500/10 text-green-700 dark:text-green-400 px-4 py-2">
                  <AlertDescription>Terminal mapping successfully created!</AlertDescription>
                </Alert>
              )}

              {mappingModalLoading ? (
                <div className="py-8 text-center text-muted-foreground px-4 py-2">
                  <Loader size="lg" label="Loading terminal details…" />
                </div>
              ) : (
                <div className="min-h-0 flex-1 space-y-3 overflow-y-auto px-4 py-2">
                  <div className="space-y-2">
                    <Label>Merchant</Label>
                    <SearchableSelect
                      className="h-9"
                      value={mappingForm.merchant}
                      options={merchantSelectOptions}
                      onChange={(value) => onMappingSelectChange('merchant', value)}
                      placeholder={loadingMerchants ? 'Loading merchants…' : 'Select merchant'}
                      searchPlaceholder="Search merchant…"
                      emptyMessage="No merchants available"
                      disabled={submittingMapping || loadingMerchants || mappingModal.mode === 'edit'}
                      aria-label="Merchant"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>
                      Merchant Terminal <span className="text-destructive">*</span>
                    </Label>
                    <SearchableSelect
                      className="h-9"
                      value={mappingForm.clientTerminal}
                      options={merchantTerminalSelectOptions}
                      onChange={(value) => onMappingSelectChange('clientTerminal', value)}
                      placeholder={
                        !mappingForm.merchant
                          ? 'Select a merchant first'
                          : loadingMerchantTerminals
                            ? 'Loading terminals…'
                            : 'Select merchant terminal'
                      }
                      searchPlaceholder="Search terminal…"
                      emptyMessage={
                        !mappingForm.merchant
                          ? 'Select a merchant first'
                          : 'No terminals available'
                      }
                      disabled={submittingMapping || !mappingForm.merchant || loadingMerchantTerminals}
                      aria-label="Merchant terminal"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Acquirer</Label>
                    <SearchableSelect
                      className="h-9"
                      value={mappingForm.acquirer}
                      options={acquirerSelectOptions}
                      onChange={(value) => onMappingSelectChange('acquirer', value)}
                      placeholder={loadingAcquirers ? 'Loading acquirers…' : 'Select acquirer'}
                      searchPlaceholder="Search acquirer…"
                      emptyMessage="No acquirers available"
                      aria-label="Acquirer"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>
                      Acquirer Terminal <span className="text-destructive">*</span>
                    </Label>
                    <SearchableSelect
                      className="h-9"
                      value={mappingForm.acquirerTerminal}
                      options={acquirerTerminalSelectOptions}
                      onChange={(value) => onMappingSelectChange('acquirerTerminal', value)}
                      placeholder={
                        !mappingForm.acquirer
                          ? 'Select an acquirer first'
                          : loadingAcquirerTerminals
                            ? 'Loading terminals…'
                            : 'Select acquirer terminal'
                      }
                      searchPlaceholder="Search terminal…"
                      emptyMessage={
                        !mappingForm.acquirer
                          ? 'Select an acquirer first'
                          : 'No active terminals'
                      }
                      disabled={submittingMapping || !mappingForm.acquirer || loadingAcquirerTerminals}
                      aria-label="Acquirer terminal"
                    />
                  </div>

                  {mappingModal.mode === 'edit' && (
                    <div className="space-y-2">
                      <Label>Status</Label>
                      <SearchableSelect
                        className="h-9"
                        value={mappingForm.status}
                        options={ENTITY_STATUS_API_OPTIONS}
                        onChange={(value) => setMappingForm((prev) => ({ ...prev, status: value }))}
                        placeholder="Select status"
                        searchPlaceholder="Search status…"
                        disabled={submittingMapping}
                        aria-label="Status"
                      />
                    </div>
                  )}

                  <div className="flex items-center justify-between gap-3 rounded-lg border px-3 py-2">
                    <div className="space-y-0">
                      <Label className="text-sm">Collect billing details on checkout</Label>
                      <p className="text-xs text-muted-foreground">
                        When on, the hosted checkout page collects the shopper's name, address,
                        city, state, zip, country, and phone the merchant doesn't need to send
                        them in their processPayment call for this mapping.
                      </p>
                    </div>
                    <Switch
                      checked={mappingForm.collectCustomerDetailsOnCheckout}
                      disabled={submittingMapping}
                      onCheckedChange={(checked) =>
                        setMappingForm((prev) => ({ ...prev, collectCustomerDetailsOnCheckout: checked }))
                      }
                      aria-label="Collect billing details on checkout"
                    />
                  </div>

                  {/* Bypass KYC toggle removed 2026-10-28: Collect Group collects identity (tax ID /
                      date of birth) on its own hosted page, so we never bypass it. Saves always send
                      testDummyValues: false (below). Un-comment to restore the toggle.
                  <div className="flex items-center justify-between gap-3 rounded-lg border px-3 py-2">
                    <div className="space-y-0">
                      <Label className="text-sm">Bypass KYC</Label>
                      <p className="text-xs text-muted-foreground">
                        Collect Group only. Skips the shopper's tax ID and date of birth step and
                        sends placeholder values instead.
                      </p>
                    </div>
                    <Switch
                      checked={mappingForm.testDummyValues}
                      disabled={submittingMapping}
                      onCheckedChange={(checked) =>
                        setMappingForm((prev) => ({ ...prev, testDummyValues: checked }))
                      }
                      aria-label="Test dummy values"
                    />
                  </div>
                  */}

                  {isCollectGroupMappingAcquirer && apmSchemeCatalog.length > 0 && (
                    <div className="space-y-2 rounded-lg border p-4">
                      <div className="space-y-0.5">
                        <Label>Alternative Payment Methods (APMs)</Label>
                        <p className="text-xs text-muted-foreground">
                          Collect Group only. Pick which APMs the hosted checkout offers for this
                          mapping — only the checked ones are shown (each is matched to the order's
                          currency). Leave all unchecked to fall back to the acquirer terminal's
                          "Enable APM" setting.
                        </p>
                      </div>
                      <div className="grid grid-cols-2 gap-2 pt-1 sm:grid-cols-3">
                        {apmSchemeCatalog.map((scheme) => {
                          const checked = mappingForm.apmSchemes.includes(scheme.slug);
                          return (
                            <label
                              key={scheme.slug}
                              className={cn(
                                'flex cursor-pointer items-center gap-2 rounded-lg border px-3 py-2 text-sm transition-colors',
                                checked ? 'border-primary bg-primary/5 ring-1 ring-primary' : 'border-input hover:bg-accent',
                              )}
                            >
                              <Checkbox
                                checked={checked}
                                disabled={submittingMapping}
                                onCheckedChange={(value) =>
                                  setMappingForm((prev) => ({
                                    ...prev,
                                    apmSchemes: value === true
                                      ? [...prev.apmSchemes, scheme.slug]
                                      : prev.apmSchemes.filter((s) => s !== scheme.slug),
                                  }))
                                }
                                aria-label={scheme.label}
                              />
                              <span className="flex flex-col">
                                <span className="font-medium">{scheme.label}</span>
                                <span className="text-[11px] text-muted-foreground">{scheme.currency}</span>
                              </span>
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {mappingForm.merchant && isPriorityMerchant && (
                    <div className="space-y-2 rounded-lg border p-4">
                      <div className="space-y-0.5">
                        <Label>Priority</Label>
                        <p className="text-xs text-muted-foreground">
                          Payments try P1 first and cascade down only if a higher priority's
                          mapping isn't Active. A tier already in use opens that mapping for
                          editing instead. Adding a bolt-on payment method (like crypto)? Pick
                          "Supplementary" below instead of a tier it's an add-on that's always
                          available, not a fallback that competes for P1-P3.
                        </p>
                      </div>
                      <div className="grid grid-cols-3 gap-3 pt-1">
                        {PRIORITY_TIERS.map((tier) => {
                          const value = String(tier.value) as '1' | '2' | '3';
                          const taken = takenPriorities.has(tier.value);
                          const selected = mappingForm.priority === value;
                          return (
                            <button
                              key={tier.value}
                              type="button"
                              disabled={submittingMapping}
                              onClick={() => {
                                if (taken) {
                                  const merchantId = Number(mappingForm.merchant);
                                  const owningRow = statsMids.find(
                                    (m) => m.merchantId === merchantId && m.priority === tier.value,
                                  );
                                  if (owningRow) void openEditMappingModal(owningRow);
                                  return;
                                }
                                setMappingForm((prev) => ({ ...prev, priority: value }));
                              }}
                              className={cn(
                                'relative flex flex-col items-start gap-1.5 rounded-lg border p-3 text-left transition-colors',
                                selected
                                  ? 'border-primary bg-primary/5 ring-1 ring-primary'
                                  : taken
                                    ? 'border-dashed hover:bg-accent'
                                    : 'border-input hover:bg-accent',
                              )}
                            >
                              {selected && (
                                <BsCheckCircleFill className="absolute right-2 top-2 text-primary" />
                              )}
                              {taken && !selected && (
                                <span className="absolute right-2 top-2 rounded-full bg-muted px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground">
                                  Edit
                                </span>
                              )}
                              <span className="flex h-7 w-7 items-center justify-center rounded-full bg-primary/10 text-sm font-semibold text-primary">
                                P{tier.value}
                              </span>
                              <span className="text-sm font-medium">{tier.label}</span>
                              <span className="text-xs text-muted-foreground">{tier.description}</span>
                            </button>
                          );
                        })}
                      </div>
                      {/* Deliberately outside the 3-tile P1-P3 grid (and not the merchant-wide "No
                          Priority" switch below, which turns priority routing off entirely): this
                          opts just THIS mapping out of the priority auto-selection without touching
                          P1-P3 at all. For a merchant whose 3 tiers are already all taken by card
                          acquirers, this is how a supplementary acquirer (e.g. a crypto option like
                          KuCoin) gets added alongside them. */}
                      <button
                        type="button"
                        disabled={submittingMapping}
                        onClick={() => {
                          if (existingBoltOnRow) {
                            void openEditMappingModal(existingBoltOnRow);
                            return;
                          }
                          setMappingForm((prev) => ({ ...prev, priority: '' }));
                        }}
                        className={cn(
                          'relative flex w-full flex-col items-start gap-1 rounded-lg border p-3 text-left transition-colors',
                          mappingForm.priority === ''
                            ? 'border-primary bg-primary/5 ring-1 ring-primary'
                            : 'border-input hover:bg-accent',
                        )}
                      >
                        {mappingForm.priority === '' && (
                          <BsCheckCircleFill className="absolute right-2 top-2 text-primary" />
                        )}
                        {existingBoltOnRow && mappingForm.priority !== '' && (
                          <span className="absolute right-2 top-2 rounded-full bg-muted px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground">
                            Edit
                          </span>
                        )}
                        <span className="text-sm font-medium">Bolt-on add-on</span>
                        <span className="text-xs text-muted-foreground">
                          An add-on payment method (e.g. crypto) that's always available alongside
                          P1-P3 not a fallback card acquirer, so it never competes for a tier.
                        </span>
                      </button>
                    </div>
                  )}

                  {mappingForm.merchant && (
                    <div className="flex items-center justify-between gap-3 rounded-lg border px-3 py-2">
                      <div className="space-y-0">
                        <Label className="text-sm">No Priority</Label>
                        <p className="text-xs text-muted-foreground">
                          {isPriorityMerchant
                            ? 'Off priority routing is required for this merchant.'
                            : 'On this mapping uses a single direct terminal.'}
                        </p>
                      </div>
                      <Switch
                        checked={!isPriorityMerchant}
                        disabled={submittingMapping}
                        onCheckedChange={(checked) => handleTogglePriorityRouting(checked)}
                        aria-label="Toggle no priority"
                      />
                    </div>
                  )}
                </div>
              )}

              <DialogFooter className="shrink-0 p-4">
                <Button
                  type="button"
                  variant="outline"
                  disabled={submittingMapping}
                  onClick={closeMappingModal}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={submittingMapping || mappingModalLoading}
                >
                  {submittingMapping
                    ? (mappingModal.mode === 'create' ? 'Creating…' : 'Saving…')
                    : (mappingModal.mode === 'create' ? 'Create Mapping' : 'Save Changes')}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        )}
      </Dialog>

      {modalData && (
        <div
          className="dh-modal-backdrop"
          onClick={() => setModalData(null)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
          }}
        >
          <div
            className="dh-modal"
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'var(--color-bg)',
              padding: '1.5rem',
              borderRadius: '12px',
              width: '100%',
              maxWidth: '400px',
              boxShadow: '0 10px 30px rgba(0,0,0,0.5)',
            }}
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '1.5rem',
              }}
            >
              <h3
                style={{
                  margin: 0,
                  color: 'var(--color-text-main)',
                  fontSize: '1.2rem',
                  fontWeight: 600,
                }}
              >
                {modalData.title}
              </h3>
              <button
                onClick={() => setModalData(null)}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: 'var(--color-text-muted)',
                  fontSize: '1.8rem',
                  cursor: 'pointer',
                  lineHeight: 1,
                }}
              >
                &times;
              </button>
            </div>
            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: '8px',
                maxHeight: '300px',
                overflowY: 'auto',
                paddingRight: '4px',
              }}
            >
              {modalData.items.map((item) => (
                <span
                  key={item}
                  className="acq-mid-chip"
                  style={{
                    margin: 0,
                    ...(modalData.isCurrency
                      ? {
                        borderColor: 'var(--color-primary-light)',
                        color: 'var(--color-primary)',
                      }
                      : {}),
                  }}
                >
                  {item}
                </span>
              ))}
            </div>
          </div>
        </div>
      )}

      {deleteTarget && (
        <DeleteConfirmDialog
          open
          title={getDeleteDialogMeta(deleteTarget).title}
          entityLabel={getDeleteDialogMeta(deleteTarget).entityLabel}
          entityName={getDeleteDialogMeta(deleteTarget).entityName}
          details={getDeleteDialogMeta(deleteTarget).details}
          loading={deleting}
          error={deleteError}
          onConfirm={confirmDelete}
          onCancel={closeDeleteDialog}
        />
      )}

      <GridColumnFilterPanel
        open={filterPanelOpen}
        onOpenChange={setFilterPanelOpen}
        fields={columnFilterFields}
        values={mergedColumnFilters}
        multiple
        onApply={handleApplyColumnFilters}
        onClear={handleClearColumnFilters}
      />

    </ListConsolePage>
  );
};

export default Terminals;
